import React, { useState, useEffect, useRef } from 'react';

const SpeedArithmeticGame = ({ gameSession, onGameComplete, onBack }) => {
  const [timeLeft, setTimeLeft] = useState(120); // 2 minutes
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [userAnswer, setUserAnswer] = useState('');
  const [questionsAnswered, setQuestionsAnswered] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [gameState, setGameState] = useState('ready'); // 'ready', 'playing', 'finished'
  const [gameData, setGameData] = useState({
    questions: [],
    answers: [],
    startTime: null,
    endTime: null
  });
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const answerInputRef = useRef(null);
  const timerRef = useRef(null);

  // Australian Curriculum-aligned grade mappings
  const AUSTRALIAN_CURRICULUM_MAPPINGS = {
    'Year 3': {
      arithmetic: { maxRange: 100, operations: ['+', '-'], fractions: false },
      description: 'Numbers to 100, addition and subtraction'
    },
    'Year 4': {
      arithmetic: { maxRange: 1000, operations: ['+', '-', '×'], fractions: false },
      description: 'Numbers to 1000, including multiplication'
    },
    'Year 5': {
      arithmetic: { maxRange: 10000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Numbers to 10,000, all operations including division'
    },
    'Year 6': {
      arithmetic: { maxRange: 100000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Numbers to 100,000, all operations and fractions'
    },
    'Year 7': {
      arithmetic: { maxRange: 1000000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Numbers to 1,000,000, advanced operations'
    },
    'Year 8': {
      arithmetic: { maxRange: 10000000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Large numbers, complex operations'
    },
    'Year 9': {
      arithmetic: { maxRange: 100000000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Advanced arithmetic, large number fluency'
    },
    'Year 10': {
      arithmetic: { maxRange: 100000000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Senior level arithmetic fluency'
    },
    'Year 11': {
      arithmetic: { maxRange: 100000000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'Advanced mathematics preparation'
    },
    'Year 12': {
      arithmetic: { maxRange: 100000000, operations: ['+', '-', '×', '÷'], fractions: true },
      description: 'University-level arithmetic skills'
    }
  };

  // Grade-aware difficulty enhancement
  const enhanceWithGrade = (baseSettings, gradeLevel) => {
    const curriculum = AUSTRALIAN_CURRICULUM_MAPPINGS[gradeLevel];
    if (!curriculum) return baseSettings; // Fallback to existing behavior

    // Adjust range based on grade level and difficulty
    const gradeMaxRange = curriculum.arithmetic.maxRange;
    const adjustedRange = baseSettings.range.map(num =>
      Math.min(num, gradeMaxRange / (baseSettings.range[1] / num))
    );

    // Filter operations appropriate for grade level
    const gradeOperations = curriculum.arithmetic.operations;
    const filteredOperations = baseSettings.operations.filter(op =>
      gradeOperations.includes(op)
    );

    return {
      ...baseSettings,
      range: adjustedRange,
      operations: filteredOperations.length > 0 ? filteredOperations : baseSettings.operations,
      gradeInfo: curriculum.description
    };
  };

  // Enhanced grade-aware difficulty settings
  const getDifficultySettings = () => {
    const difficulty = gameSession?.difficulty || 'easy';
    const gradeLevel = gameSession?.grade_level;
    const settings = gameSession?.game?.difficulty_levels?.[difficulty];

    // Handle both array format [min, max] and single number format (max)
    const normalizeRange = (range, defaultMax) => {
      if (Array.isArray(range)) {
        return range;
      } else if (typeof range === 'number') {
        return [1, range];
      } else {
        return [1, defaultMax];
      }
    };

    const operations = settings?.operations || [];

    // Get traditional difficulty settings (existing behavior)
    let baseSettings;
    switch (difficulty) {
      case 'easy':
        baseSettings = {
          range: normalizeRange(settings?.range, 12),
          operations: operations.length > 0 ? operations : ['+', '-']
        };
        break;
      case 'medium':
        baseSettings = {
          range: normalizeRange(settings?.range, 50),
          operations: operations.length > 0 ? operations : ['+', '-', '×']
        };
        break;
      case 'hard':
        baseSettings = {
          range: normalizeRange(settings?.range, 100),
          operations: operations.length > 0 ? operations : ['+', '-', '×', '÷']
        };
        break;
      default:
        baseSettings = { range: [1, 12], operations: ['+', '-'] };
    }

    // Enhance with grade level if available (progressive enhancement)
    if (gradeLevel) {
      return enhanceWithGrade(baseSettings, gradeLevel);
    }

    // Fallback to existing behavior for users without grade
    return baseSettings;
  };

  // Generate random number within range
  const randomNumber = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

  // Generate new question
  const generateQuestion = () => {
    const { range, operations } = getDifficultySettings();
    const [min, max] = range;
    const operation = operations[Math.floor(Math.random() * operations.length)];
    
    let num1 = randomNumber(min, max);
    let num2 = randomNumber(min, max);
    let correctAnswer;

    switch (operation) {
      case '+':
        correctAnswer = num1 + num2;
        break;
      case '-':
        // Ensure positive result
        if (num1 < num2) [num1, num2] = [num2, num1];
        correctAnswer = num1 - num2;
        break;
      case '×':
        // Keep numbers smaller for multiplication
        num1 = randomNumber(min, Math.min(max, 12));
        num2 = randomNumber(min, Math.min(max, 12));
        correctAnswer = num1 * num2;
        break;
      case '÷':
        // Generate division that results in whole numbers
        correctAnswer = randomNumber(min, Math.min(max, 12));
        num2 = randomNumber(2, Math.min(max, 12));
        num1 = correctAnswer * num2;
        break;
      default:
        correctAnswer = num1 + num2;
    }

    return {
      num1,
      num2,
      operation,
      correctAnswer,
      questionText: `${num1} ${operation} ${num2}`,
      timestamp: Date.now()
    };
  };

  // Start the game
  const startGame = () => {
    setGameState('playing');
    setTimeLeft(120);
    setQuestionsAnswered(0);
    setCorrectAnswers(0);
    setGameData({
      questions: [],
      answers: [],
      startTime: Date.now(),
      endTime: null
    });
    
    const firstQuestion = generateQuestion();
    setCurrentQuestion(firstQuestion);
    
    // Start timer
    timerRef.current = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          endGame();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Submit answer
  const submitAnswer = () => {
    if (!currentQuestion || userAnswer.trim() === '' || gameState !== 'playing') return;

    const userNum = parseInt(userAnswer.trim());
    const correct = userNum === currentQuestion.correctAnswer;
    
    // Update game data
    setGameData(prev => ({
      ...prev,
      questions: [...prev.questions, currentQuestion],
      answers: [...prev.answers, {
        userAnswer: userNum,
        correctAnswer: currentQuestion.correctAnswer,
        isCorrect: correct,
        timeSpent: Date.now() - currentQuestion.timestamp
      }]
    }));

    setQuestionsAnswered(prev => prev + 1);
    if (correct) {
      setCorrectAnswers(prev => prev + 1);
    }

    // Show feedback
    setIsCorrect(correct);
    setShowFeedback(true);
    
    // Clear input and generate next question after brief feedback
    setTimeout(() => {
      setShowFeedback(false);
      setUserAnswer('');
      
      if (gameState === 'playing') {
        const nextQuestion = generateQuestion();
        setCurrentQuestion(nextQuestion);
        
        // Focus back to input
        if (answerInputRef.current) {
          answerInputRef.current.focus();
        }
      }
    }, 800);
  };

  // End the game
  const endGame = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    
    setGameState('finished');
    
    // Use callback to get the most current gameData state
    setGameData(currentGameData => {
      const finalGameData = {
        ...currentGameData,
        endTime: Date.now()
      };
      
      const totalTime = finalGameData.endTime - finalGameData.startTime;
      const totalQuestions = finalGameData.answers.length;
      const correctCount = finalGameData.answers.filter(answer => answer.isCorrect).length;
      const score = totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
      const timeTakenSeconds = Math.floor(totalTime / 1000);
      
      const results = {
        score,
        timeTaken: timeTakenSeconds,
        questionsAttempted: totalQuestions,
        correctAnswers: correctCount,
        gameData: finalGameData,
        pointsEarned: correctCount + Math.max(0, 60 - timeTakenSeconds) // base points + time bonus
      };
      
      onGameComplete(results);
      return finalGameData;
    });
  };

  // Handle key press
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && gameState === 'playing') {
      submitAnswer();
    }
  };

  // Format time display
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Focus input when question changes
  useEffect(() => {
    if (gameState === 'playing' && answerInputRef.current) {
      answerInputRef.current.focus();
    }
  }, [currentQuestion, gameState]);

  if (gameState === 'ready') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-red-500 p-8 text-white text-center">
            <div className="text-5xl mb-4">⚡</div>
            <h1 className="text-3xl font-bold mb-2">Speed Arithmetic</h1>
            <p className="text-orange-100">
              Difficulty: <span className="font-semibold capitalize">{gameSession?.difficulty}</span>
            </p>
          </div>

          {/* Instructions */}
          <div className="p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Game Instructions</h2>
            
            <div className="space-y-4 mb-8">
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-600 font-bold">1</div>
                <div>You have <strong>2 minutes</strong> to solve as many problems as possible</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-600 font-bold">2</div>
                <div>Type your answer and press <strong>Enter</strong> to submit</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-600 font-bold">3</div>
                <div>Focus on <strong>accuracy</strong> first, then speed</div>
              </div>
              <div className="flex items-center text-gray-700">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-4 text-blue-600 font-bold">4</div>
                <div>Earn <strong>1 point per correct answer</strong> + time bonus</div>
              </div>
            </div>

            {/* Difficulty Info */}
            <div className="bg-orange-50 rounded-lg p-4 mb-8">
              <h3 className="font-semibold text-orange-900 mb-2">
                {gameSession?.difficulty?.charAt(0).toUpperCase() + gameSession?.difficulty?.slice(1)} Mode
                {gameSession?.grade_level && (
                  <span className="text-sm font-normal text-orange-700 ml-2">
                    • {gameSession.grade_level}
                  </span>
                )}
              </h3>
              <p className="text-orange-800 text-sm">
                {getDifficultySettings().range[0]}-{getDifficultySettings().range[1]} range, {getDifficultySettings().operations.join(', ')} operations
              </p>
              {getDifficultySettings().gradeInfo && (
                <p className="text-orange-700 text-xs mt-1">
                  Australian Curriculum: {getDifficultySettings().gradeInfo}
                </p>
              )}
            </div>

            {/* Action buttons */}
            <div className="flex gap-4">
              <button
                onClick={onBack}
                className="flex-1 bg-gray-100 hover:bg-gray-200 text-gray-700 py-3 px-6 rounded-lg font-medium transition-colors"
              >
                Back to Games
              </button>
              <button
                onClick={startGame}
                className="flex-2 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white py-3 px-8 rounded-lg font-medium transition-all duration-200 transform hover:scale-105"
              >
                Start Game! 🚀
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (gameState === 'playing') {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
          {/* Game Header */}
          <div className="bg-gradient-to-r from-orange-400 to-red-500 p-6 text-white">
            <div className="flex justify-between items-center">
              <div className="text-center">
                <div className="text-2xl font-bold">{formatTime(timeLeft)}</div>
                <div className="text-sm text-orange-100">Time Left</div>
              </div>
              
              <div className="text-4xl">⚡</div>
              
              <div className="text-center">
                <div className="text-2xl font-bold">{correctAnswers}/{questionsAnswered}</div>
                <div className="text-sm text-orange-100">Correct</div>
              </div>
            </div>
          </div>

          {/* Question Display */}
          <div className="p-12 text-center">
            {showFeedback ? (
              <div className={`text-6xl font-bold mb-8 ${isCorrect ? 'text-green-500' : 'text-red-500'}`}>
                {isCorrect ? '✅ Correct!' : '❌ Wrong'}
                {!isCorrect && currentQuestion && (
                  <div className="text-2xl text-gray-600 mt-4">
                    Answer: {currentQuestion.correctAnswer}
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="text-6xl font-bold text-gray-800 mb-8 font-mono">
                  {currentQuestion?.questionText || ''}
                </div>
                
                <div className="text-4xl text-gray-600 mb-8">=</div>
                
                <input
                  ref={answerInputRef}
                  type="number"
                  value={userAnswer}
                  onChange={(e) => setUserAnswer(e.target.value)}
                  onKeyPress={handleKeyPress}
                  className="text-4xl text-center border-b-4 border-orange-400 bg-transparent focus:outline-none focus:border-orange-600 font-mono w-32"
                  placeholder="?"
                  autoFocus
                />
                
                <div className="mt-8">
                  <button
                    onClick={submitAnswer}
                    disabled={!userAnswer.trim()}
                    className="bg-orange-500 hover:bg-orange-600 disabled:bg-gray-300 text-white py-3 px-8 rounded-lg font-medium transition-colors"
                  >
                    Submit (Enter)
                  </button>
                </div>
              </>
            )}
          </div>

          {/* Progress Bar */}
          <div className="px-6 pb-6">
            <div className="bg-gray-200 rounded-full h-2">
              <div 
                className="bg-orange-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(correctAnswers / Math.max(questionsAnswered, 1)) * 100}%` }}
              ></div>
            </div>
            <div className="text-center text-sm text-gray-600 mt-2">
              Accuracy: {questionsAnswered > 0 ? Math.round((correctAnswers / questionsAnswered) * 100) : 0}%
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null; // Game finished state is handled by parent component
};

export default SpeedArithmeticGame;